import React, { useEffect, useState, useRef, useLayoutEffect, useCallback } from 'react';
import { supabase } from './supabaseClient';
import './App.css';
import CalisanListesi from "./components/CalisanListesi";
import CalisanDetay from "./components/CalisanDetay";
import PersonelYonetimi from "./components/PersonelYonetimi";
import AdminPanel from "./components/AdminPanel";
import AdminLogin from "./components/AdminLogin";
import TumCalisanlar from "./components/TumCalisanlar";
import HataBildirimleriListesi from "./components/HataBildirimleriListesi";
import HesapOlustur from "./components/HesapOlustur";
import GirisYap from "./components/GirisYap";
import Modal from "./components/Modal";
import ErrorBoundary from "./components/ErrorBoundary";
import LoadingSpinner from "./components/LoadingSpinner";
import MaasHesabi from "./components/MaasHesabi";
import Maasim from "./components/Maasim";
import IzinTalepleri from "./components/IzinTalepleri";

function App() {
  const [personeller, setPersoneller] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seciliCalisan, setSeciliCalisan] = useState(null);
  const [activeTab, setActiveTab] = useState('calisanlar'); // 'calisanlar' | 'personel' | 'admin' | 'hesapOlustur'
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [showHesapOlustur, setShowHesapOlustur] = useState(false);
  const [showGirisYap, setShowGirisYap] = useState(false);
  const [session, setSession] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const [passwordResetLoading, setPasswordResetLoading] = useState(false);
  const [passwordResetError, setPasswordResetError] = useState('');
  const [passwordResetSuccess, setPasswordResetSuccess] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
  const [accountMenuPosition, setAccountMenuPosition] = useState(null);
  const [hasPendingIzin, setHasPendingIzin] = useState(false);
  const [hasPendingHata, setHasPendingHata] = useState(false);
  const accountMenuRef = useRef(null);
  const profileBtnRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const mobileMenuBtnRef = useRef(null);
  const [mobileMenuPosition, setMobileMenuPosition] = useState(null);
  const [isMobileViewport, setIsMobileViewport] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches
  );

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const onChange = (e) => setIsMobileViewport(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    if (!isMobileViewport) {
      setIsMobileMenuOpen(false);
    }
  }, [isMobileViewport]);

  useEffect(() => {
    if (!isAccountMenuOpen && !isMobileMenuOpen) return;

    function handleClickOutside(event) {
      if (
        isAccountMenuOpen &&
        accountMenuRef.current &&
        !accountMenuRef.current.contains(event.target)
      ) {
        setIsAccountMenuOpen(false);
      }
      if (
        isMobileMenuOpen &&
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target)
      ) {
        setIsMobileMenuOpen(false);
      }
    }

    function handleEscape(event) {
      if (event.key === "Escape") {
        setIsAccountMenuOpen(false);
        setIsMobileMenuOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isAccountMenuOpen, isMobileMenuOpen]);

  const updateAccountMenuPosition = useCallback(() => {
    if (!profileBtnRef.current) return;

    const rect = profileBtnRef.current.getBoundingClientRect();
    const menuWidth = Math.min(260, window.innerWidth - 24);
    const gap = 8;
    const margin = 12;

    let left = rect.right - menuWidth;
    if (left < margin) left = margin;
    if (left + menuWidth > window.innerWidth - margin) {
      left = window.innerWidth - menuWidth - margin;
    }

    setAccountMenuPosition({
      top: rect.bottom + gap,
      left,
      width: menuWidth,
    });
  }, []);

  useLayoutEffect(() => {
    if (!isAccountMenuOpen) {
      setAccountMenuPosition(null);
      return;
    }

    updateAccountMenuPosition();
    window.addEventListener("resize", updateAccountMenuPosition);
    window.addEventListener("scroll", updateAccountMenuPosition, true);
    return () => {
      window.removeEventListener("resize", updateAccountMenuPosition);
      window.removeEventListener("scroll", updateAccountMenuPosition, true);
    };
  }, [isAccountMenuOpen, updateAccountMenuPosition]);

  const updateMobileMenuPosition = useCallback(() => {
    if (!mobileMenuBtnRef.current) return;

    const rect = mobileMenuBtnRef.current.getBoundingClientRect();
    const menuWidth = Math.min(Math.max(rect.width, 220), window.innerWidth - 24);
    const gap = 8;
    const margin = 12;

    let left = rect.left;
    if (left + menuWidth > window.innerWidth - margin) {
      left = window.innerWidth - menuWidth - margin;
    }
    if (left < margin) left = margin;

    setMobileMenuPosition({
      top: rect.bottom + gap,
      left,
      width: menuWidth,
    });
  }, []);

  useLayoutEffect(() => {
    if (!isMobileMenuOpen || !isMobileViewport) {
      setMobileMenuPosition(null);
      return;
    }

    updateMobileMenuPosition();
    window.addEventListener("resize", updateMobileMenuPosition);
    window.addEventListener("scroll", updateMobileMenuPosition, true);
    return () => {
      window.removeEventListener("resize", updateMobileMenuPosition);
      window.removeEventListener("scroll", updateMobileMenuPosition, true);
    };
  }, [isMobileMenuOpen, isMobileViewport, updateMobileMenuPosition]);

  async function fetchPersoneller() {
    const { data, error } = await supabase
      .from('personel')
      .select('kullanici_id, isim, soyisim, ise_giris_tarihi, aktif')
      .order('kullanici_id', { ascending: true });

    if (error) {
      console.error('Veri çekme hatası:', error);
      setPersoneller([]);
    } else {
      setPersoneller(data || []);
    }
    setLoading(false);
  }

  useEffect(() => {
    fetchPersoneller();
  }, []);

  // Kullanıcı profilini yükle
  useEffect(() => {
    async function loadUserProfile() {
      if (!session?.user) {
        setUserProfile(null);
        setProfileLoading(false);
        return;
      }
      
      setProfileLoading(true);
      
      try {
        const email = session?.user?.email || null;
        
        // Önce kullanici_profilleri tablosundan kontrol et
        let { data, error } = await supabase
          .from("kullanici_profilleri")
          .select("*")
          .eq("email", email)
          .maybeSingle();
        
        if (data) {
          setUserProfile(data);
        } else {
          // Profil bulunamadı - admin_users tablosundan kontrol et
          // admin_users tablosunda email yok, user_id var
          // Önce auth.users tablosundan user_id'yi bul
          const { data: authUser } = await supabase.auth.getUser();
          
          if (authUser?.user?.id) {
            const adminCheck = await supabase
              .from("admin_users")
              .select("user_id")
              .eq("user_id", authUser.user.id)
              .maybeSingle();
            
            console.log("Admin kontrolü sonucu:", adminCheck);
            
            if (adminCheck.data) {
              // Admin kullanıcı - geçici admin profili oluştur
              const tempAdminProfile = {
                id: -1,
                kullanici_id: null,
                email: email,
                isim: "Admin",
                soyisim: "Kullanıcı",
                is_admin: true,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
              };
              console.log("Geçici admin profili oluşturuldu:", tempAdminProfile);
              setUserProfile(tempAdminProfile);
            } else {
              console.log("Admin değil, profil yok");
              setUserProfile(null);
            }
          } else {
            console.log("Auth user bulunamadı");
            setUserProfile(null);
          }
        }
      } catch (err) {
        console.error("Profil yükleme hatası:", err);
        setUserProfile(null);
      } finally {
        setProfileLoading(false);
      }
    }

    loadUserProfile();
  }, [session?.user?.id, session?.user?.email]);

  // Admin: bekleyen izin / hata bildirimi göstergesi
  useEffect(() => {
    if (!userProfile?.is_admin) {
      setHasPendingIzin(false);
      setHasPendingHata(false);
      return;
    }

    let cancelled = false;

    async function loadPendingBadges() {
      try {
        const [izinRes, hataRes] = await Promise.all([
          supabase
            .from("izin_talepleri")
            .select("id", { count: "exact", head: true })
            .eq("durum", "beklemede"),
          supabase
            .from("hata_bildirimleri")
            .select("id", { count: "exact", head: true })
            .eq("durum", "beklemede"),
        ]);

        if (cancelled) return;
        setHasPendingIzin((izinRes.count || 0) > 0);
        setHasPendingHata((hataRes.count || 0) > 0);
      } catch (err) {
        console.error("Bekleyen talep göstergesi hatası:", err);
      }
    }

    loadPendingBadges();
    const intervalId = setInterval(loadPendingBadges, 60000);
    const onFocus = () => loadPendingBadges();
    window.addEventListener("focus", onFocus);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
      window.removeEventListener("focus", onFocus);
    };
  }, [userProfile?.is_admin, activeTab]);

  useEffect(() => {
    // İlk yüklemede session'ı al
    const getInitialSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        console.log('Initial session:', session);
        setSession(session);
      } catch (error) {
        console.error('Session getirme hatası:', error);
        setSession(null);
      }
    };

    getInitialSession();

    // Auth state değişikliklerini dinle
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('Auth state change:', event, session);
      
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        console.log('Kullanıcı giriş yaptı veya token yenilendi');
        setSession(session);
      } else if (event === 'SIGNED_OUT') {
        console.log('Kullanıcı çıkış yaptı');
        setSession(null);
        setUserProfile(null);
      } else if (event === 'INITIAL_SESSION') {
        console.log('İlk session yüklendi');
        setSession(session);
      } else if (event === 'PASSWORD_RECOVERY') {
        console.log('Şifre sıfırlama modu');
        setSession(session);
        setShowPasswordReset(true);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  async function handlePasswordUpdate(e) {
    e.preventDefault();
    if (newPassword.length < 6) {
      setPasswordResetError('Şifre en az 6 karakter olmalıdır.');
      return;
    }
    if (newPassword !== newPasswordConfirm) {
      setPasswordResetError('Şifreler eşleşmiyor.');
      return;
    }

    setPasswordResetLoading(true);
    setPasswordResetError('');

    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) {
        setPasswordResetError(error.message);
      } else {
        setPasswordResetSuccess(true);
        setNewPassword('');
        setNewPasswordConfirm('');
        setTimeout(() => {
          setShowPasswordReset(false);
          setPasswordResetSuccess(false);
          // URL'deki hash parametrelerini temizle
          window.history.replaceState(null, '', window.location.pathname);
        }, 2000);
      }
    } catch (err) {
      setPasswordResetError(String(err.message || err));
    } finally {
      setPasswordResetLoading(false);
    }
  }

  if (loading) return <div className="container"><LoadingSpinner className="loader-wrap--page" /></div>;

  return (
    <ErrorBoundary>
      {/* Şifre Sıfırlama Modal */}
      {showPasswordReset && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 9999,
        }}>
          <div style={{
            backgroundColor: 'white', borderRadius: '12px', padding: '32px',
            maxWidth: '400px', width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          }}>
            <h2 style={{ marginBottom: '20px', textAlign: 'center', color: '#374151' }}>
              Yeni Şifre Belirle
            </h2>

            {passwordResetSuccess ? (
              <div style={{
                padding: '16px', backgroundColor: '#dcfce7',
                border: '1px solid #10b981', borderRadius: '8px',
                textAlign: 'center', color: '#166534', fontSize: '14px',
              }}>
                ✅ Şifreniz başarıyla güncellendi! Yönlendiriliyorsunuz...
              </div>
            ) : (
              <form onSubmit={handlePasswordUpdate} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                    Yeni Şifre *
                  </label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={6}
                    placeholder="En az 6 karakter"
                    style={{
                      width: '100%', padding: '10px 12px',
                      border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px',
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                    Yeni Şifre (Tekrar) *
                  </label>
                  <input
                    type="password"
                    value={newPasswordConfirm}
                    onChange={(e) => setNewPasswordConfirm(e.target.value)}
                    required
                    minLength={6}
                    placeholder="Şifreyi tekrar girin"
                    style={{
                      width: '100%', padding: '10px 12px',
                      border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px',
                    }}
                  />
                </div>

                {passwordResetError && (
                  <div style={{
                    padding: '8px 12px', backgroundColor: '#fee2e2',
                    border: '1px solid #fecaca', borderRadius: '6px',
                    color: '#dc2626', fontSize: '14px',
                  }}>
                    {passwordResetError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={passwordResetLoading}
                  style={{
                    padding: '12px', backgroundColor: passwordResetLoading ? '#9ca3af' : '#3b82f6',
                    color: 'white', border: 'none', borderRadius: '6px',
                    fontSize: '14px', fontWeight: '500',
                    cursor: passwordResetLoading ? 'not-allowed' : 'pointer',
                    transition: 'background-color 0.2s',
                  }}
                >
                  {passwordResetLoading ? 'Güncelleniyor...' : 'Şifreyi Güncelle'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}

      <div className="App container">
        <div className="toolbar">
          <div className="toolbar-header">
            <div className="toolbar-left">
              <img src="/logo.svg" alt="Lulus Personel" className="app-logo" />
            </div>
            <div className="toolbar-right">
              <div className={`account${session ? " account--logged-in" : ""}`} ref={accountMenuRef}>
                {session ? (
                  <>
                    <button
                      ref={profileBtnRef}
                      type="button"
                      className="account-profile-btn"
                      onClick={() => {
                        setIsMobileMenuOpen(false);
                        setIsAccountMenuOpen((open) => !open);
                      }}
                      aria-expanded={isAccountMenuOpen}
                      aria-haspopup="true"
                      aria-label="Hesap menüsü"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <circle cx="12" cy="8" r="4" />
                        <path d="M4 20c0-4 3.6-6 8-6s8 2 8 6" />
                      </svg>
                    </button>
                    {isAccountMenuOpen && accountMenuPosition && (
                      <div
                        className="account-menu"
                        style={{
                          top: accountMenuPosition.top,
                          left: accountMenuPosition.left,
                          width: accountMenuPosition.width,
                        }}
                      >
                        <div className="account-menu-email">{session.user?.email}</div>
                        <button
                          type="button"
                          className="account-logout-btn"
                          onClick={async () => {
                            try {
                              await supabase.auth.signOut();
                              setSession(null);
                              setUserProfile(null);
                              setIsAccountMenuOpen(false);
                              localStorage.clear();
                              sessionStorage.clear();
                            } catch (error) {
                              console.error("Çıkış hatası:", error);
                            }
                          }}
                        >
                          Çıkış
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <button className="tab" onClick={() => setShowHesapOlustur(true)}>Hesap Oluştur</button>
                    <button className="tab" onClick={() => setShowGirisYap(true)}>Giriş Yap</button>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="toolbar-center" ref={mobileMenuRef}>
            <button
              ref={mobileMenuBtnRef}
              type="button"
              className="mobile-menu-btn"
              onClick={() => {
                setIsAccountMenuOpen(false);
                setIsMobileMenuOpen((open) => !open);
              }}
              aria-expanded={isMobileMenuOpen}
              aria-haspopup="true"
            >
              {isMobileMenuOpen ? "✖ Menüyü Kapat" : "☰ Menü"}
            </button>
            <div
              className={`tabs${isMobileViewport && isMobileMenuOpen ? " mobile-open" : ""}${isMobileViewport ? " tabs--dropdown" : ""}`}
              style={
                isMobileViewport && isMobileMenuOpen && mobileMenuPosition
                  ? {
                      top: mobileMenuPosition.top,
                      left: mobileMenuPosition.left,
                      width: mobileMenuPosition.width,
                    }
                  : undefined
              }
            >
              <button
                className={`tab ${activeTab === 'calisanlar' ? 'tab-active' : ''}`}
                onClick={() => { setActiveTab('calisanlar'); setIsMobileMenuOpen(false); }}
              >
                Çalışanlar
              </button>
              {/* Admin sekmeleri - sadece admin kullanıcılar görebilir */}
              {userProfile?.is_admin && (
                <>
                  <button
                    className={`tab ${activeTab === 'personel' ? 'tab-active' : ''}`}
                    onClick={() => { setActiveTab('personel'); setIsMobileMenuOpen(false); }}
                  >
                    Personel Yönetimi
                  </button>
                  <button
                    className={`tab ${activeTab === 'tumCalisanlar' ? 'tab-active' : ''}`}
                    onClick={() => { setActiveTab('tumCalisanlar'); setIsMobileMenuOpen(false); }}
                  >
                    Çalışan Raporu
                  </button>
                  <button
                    className={`tab ${activeTab === 'maasHesabi' ? 'tab-active' : ''}`}
                    onClick={() => { setActiveTab('maasHesabi'); setIsMobileMenuOpen(false); }}
                  >
                    Maaş Hesabı
                  </button>
                  <button className={`tab ${activeTab === 'admin' ? 'tab-active' : ''}`} onClick={() => { setActiveTab('admin'); setIsMobileMenuOpen(false); }}>Admin</button>
                </>
              )}
              
              {/* Maaşım - sadece normal kullanıcılar görebilir */}
              {!userProfile?.is_admin && (
                <button 
                  className={`tab ${activeTab === 'maasim' ? 'tab-active' : ''}`} 
                  onClick={() => { setActiveTab('maasim'); setIsMobileMenuOpen(false); }}
                >
                  Maaşım
                </button>
              )}
              
              {/* İzin Talepleri - hem normal kullanıcılar hem admin kullanıcılar görebilir */}
              <button 
                className={`tab ${activeTab === 'izinTalepleri' ? 'tab-active' : ''}`} 
                onClick={() => { setActiveTab('izinTalepleri'); setIsMobileMenuOpen(false); }}
              >
                İzin Talepleri
                {userProfile?.is_admin && hasPendingIzin && (
                  <span className="tab-alert-dot" aria-label="Bekleyen izin talebi var" />
                )}
              </button>
              {/* Hata Bildirimleri - hem normal kullanıcılar hem admin kullanıcılar görebilir */}
              <button 
                className={`tab ${activeTab === 'hataBildirimleri' ? 'tab-active' : ''}`} 
                onClick={() => { setActiveTab('hataBildirimleri'); setIsMobileMenuOpen(false); }}
              >
                Hata Bildirimleri
                {userProfile?.is_admin && hasPendingHata && (
                  <span className="tab-alert-dot" aria-label="Bekleyen hata bildirimi var" />
                )}
              </button>
            </div>
          </div>
        </div>

        {activeTab === 'personel' && userProfile?.is_admin ? (
          <div className="card">
            <PersonelYonetimi onChanged={() => {
              // Personel listesini yeniden yükle
              fetchPersoneller();
            }} />
          </div>
        ) : activeTab === 'tumCalisanlar' && userProfile?.is_admin ? (
          <div className="card">
            <TumCalisanlar />
          </div>
        ) : activeTab === 'admin' && userProfile?.is_admin ? (
          <div className="card">
            <AdminPanel />
          </div>
        ) : activeTab === 'izinTalepleri' ? (
          <div className="card">
            <IzinTalepleri />
          </div>
        ) : activeTab === 'hataBildirimleri' ? (
          <div className="card">
            <HataBildirimleriListesi />
          </div>
        ) : activeTab === 'maasHesabi' && userProfile?.is_admin ? (
          <div className="card">
            <MaasHesabi />
          </div>
        ) : activeTab === 'maasim' && !userProfile?.is_admin ? (
          <div className="card">
            <Maasim session={session} userProfile={userProfile} />
          </div>
        ) : (
          <div className={isMobileViewport ? undefined : "layout"}>
            <div className="card">
              <CalisanListesi
                personeller={personeller}
                onCalisanSelect={(calisan) => setSeciliCalisan(calisan)}
                seciliCalisan={seciliCalisan}
                session={session}
                userProfile={userProfile}
                profileLoading={profileLoading}
              />
            </div>
            {!isMobileViewport && seciliCalisan && (
              <div className="card">
                <CalisanDetay calisan={seciliCalisan} />
              </div>
            )}
          </div>
        )}

        <Modal
          open={isMobileViewport && !!seciliCalisan}
          onClose={() => setSeciliCalisan(null)}
          wide
          title={
            seciliCalisan
              ? `${(seciliCalisan.isim || seciliCalisan.soyisim)
                  ? `${seciliCalisan.isim || ""} ${seciliCalisan.soyisim || ""}`.trim()
                  : `Kullanıcı ${seciliCalisan.kullanici_id}`} — Çalışma Saatleri`
              : ""
          }
        >
          {seciliCalisan && (
            <CalisanDetay calisan={seciliCalisan} inDialog />
          )}
        </Modal>

                <Modal open={showHesapOlustur} onClose={() => setShowHesapOlustur(false)} title="Hesap Oluştur">
          <HesapOlustur onSuccess={() => setShowHesapOlustur(false)} />
        </Modal>
        
        <Modal open={showGirisYap} onClose={() => setShowGirisYap(false)} title="Giriş Yap">
          <GirisYap onSuccess={() => setShowGirisYap(false)} />
        </Modal>
      </div>
    </ErrorBoundary>
  );
}

export default App;
